import { ReactElement, useEffect } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { useQuery, gql } from '@apollo/client'

import { isEmpty } from 'lodash'
import { SearchCheckboxes } from 'components/SearchCheckboxes'
import { normalize } from 'utils/string'
import { ISite, ISitesCheckboxesProps } from './types'

const messages = defineMessages({
  allSites: {
    id: 'sites.checkboxes.all',
    defaultMessage: 'All PoS'
  },
  error: {
    id: 'sites.checkboxes.error',
    defaultMessage: 'Error loading your PoS'
  },
  actionOnError: {
    id: 'sites.checkboxes.actionOnError',
    defaultMessage: 'Try again'
  },
  empty: {
    id: 'sites.checkboxes.empty',
    defaultMessage: 'No PoS found'
  },
  allOption: {
    id: 'sites.checkboxes.all',
    defaultMessage: 'All PoS'
  },
  searchPlaceholder: {
    id: 'sites.checkboxes.searchPlaceholder',
    defaultMessage: 'Search PoS'
  }
})

const GET_SITES_AUTOCOMPLETE = gql`
  query GetSitesAutocomplete($limit: Int!) {
    sitesAutocomplete(input: { limit: $limit }) {
      id
      name
      companyID
    }
  }
`

const getItemLabel = ({ name }: ISite): string => {
  return name
}

export const SitesCheckboxes = ({ siteIds = [], companyIds, ...props }: ISitesCheckboxesProps): ReactElement => {
  const intl = useIntl()
  const { data, loading, error, refetch } = useQuery(GET_SITES_AUTOCOMPLETE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      limit: 1000
    }
  })

  useEffect(() => {
    refetch()
  }, [])

  const getInputValue = (): string => {
    if (data && data.sitesAutocomplete) {
      if (siteIds.length > 0) {
        return siteIds
          .map((siteId) => {
            const site = data.sitesAutocomplete.find(({ id }: { id: string }) => id === siteId)

            if (site) {
              return site.name
            }

            return ''
          })
          .filter((siteName) => !isEmpty(siteName))
          .join(', ')
      }

      return intl.formatMessage(messages.allSites)
    }

    return ''
  }

  const getShownItems = (): ISite[] => {
    if (data && data.sitesAutocomplete) {
      if (companyIds.length === 0) {
        return data.sitesAutocomplete
      }
      return data.sitesAutocomplete.filter(({ companyID }: { companyID: string }) => companyIds.indexOf(companyID) > -1)
    }

    return []
  }

  return (
    <SearchCheckboxes
      items={getShownItems()}
      loading={loading}
      error={error && intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      values={siteIds}
      inputValue={getInputValue()}
      allOption={intl.formatMessage(messages.allOption)}
      getItemLabel={getItemLabel}
      getTagLabel={(id: string): string => data && data.sitesAutocomplete.find((site: ISite) => site.id === id).name}
      searchPlaceholder={intl.formatMessage(messages.searchPlaceholder)}
      filterItem={(item: ISite, filter: string): boolean =>
        normalize(getItemLabel(item)).indexOf(normalize(filter)) > -1
      }
      {...props}
    />
  )
}
